import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  InputLabel,
  TableRow,
  MenuItem,
  TablePagination,
  FormControl,
  Autocomplete,
  TextField,
  IconButton,
  Select,
  Box,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  FormHelperText,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import Swal from 'sweetalert2';
import { gridSpacing } from 'config.js';

// API URLs for Course Category
const apiUrl = 'https://api.academy.eurekagroup.id/course-categories';
const addCategoryUrl = `${apiUrl}/add`;
const editCategoryUrl = `${apiUrl}/edit`;
const deleteCategoryUrl = (id) => `${apiUrl}/delete/${id}`;
const jobPositionApiUrl = 'https://apidashboard.eurekagroup.id/job-positions';

const CategoryCoursePage = () => {
  const [data, setData] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [selectedJobPosition, setSelectedJobPosition] = useState('');
  const [selectedJobPositions, setSelectedJobPositions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [titleCategory, setTitleCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [slugCategory, setSlugCategory] = useState('');
  const [descCategory, setDescCategory] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [preview, setPreview] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [errors, setErrors] = useState({
      titleCategory: false,
      slugCategory: false,
      descCategory: false,
      selectedJobPositions: false,
      isActive: false,
      thumbnail: false,
    });    

    useEffect(() => {
      const fetchCategories = async () => {
        try {
          const response = await axios.get(apiUrl);
          setData(response.data); // Perbarui state dengan data dari server
        } catch (error) {
          console.error('Error fetching course categories:', error);
        }
      };
    
      const fetchJobPositions = async () => {
        try {
          const response = await axios.get(jobPositionApiUrl);
          setJobPositions(response.data);
        } catch (error) {
          console.error('Error fetching job positions:', error);
        }
      };
    
      fetchCategories(); // Panggil saat komponen mount
      fetchJobPositions();
    }, []);    

  const handleSearchChange = (event) => setSearchText(event.target.value);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (category) => {
    if (category) {
      setSelectedCategory(category);
      setTitleCategory(category.title_category);
      setSlugCategory(category.slug_category);
      setDescCategory(category.desc_category);
      setIsActive(category.is_active);
  
      // Pastikan untuk mencocokkan semua id_jobposition dalam array
      const jobPositionIds = category.id_jobposition.map(job => job._id); // Ambil semua _id dari id_jobposition
      setSelectedJobPositions(jobPositions.filter(pos => jobPositionIds.includes(pos._id))); // Filter sesuai _id
    } else {
      setSelectedCategory(null);
      setTitleCategory('');
      setSlugCategory('');
      setDescCategory('');
      setIsActive(true);
      setSelectedJobPositions([]); // Reset job positions
    }
    setOpenDialog(true); // Buka dialog
  };  

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSaveCategory = async () => {
    setIsLoading(true); // Start loading
  
    // Validation logic
    const newErrors = {
      titleCategory: !titleCategory,
      slugCategory: !slugCategory,
      descCategory: !descCategory,
      selectedJobPositions: selectedJobPositions.length === 0,
      isActive: isActive === null,
      thumbnail: !thumbnail && !preview,
    };
  
    setErrors(newErrors);
  
    // Jika ada error, hentikan proses
    if (Object.values(newErrors).includes(true)) {
      setIsLoading(false);
      return;
    }
  
    const formData = new FormData();
    formData.append("title_category", titleCategory);
    formData.append("slug_category", slugCategory);
    formData.append("desc_category", descCategory);
    formData.append("is_active", isActive);
  
    let jobPositionIds = selectedJobPositions.map((job) => job._id);
    if (jobPositionIds.includes("all")) {
      jobPositionIds = ["all"];
    }
  
    jobPositionIds.forEach((id) => {
      formData.append("id_jobposition", id);
    });
  
    if (thumbnail) {
      formData.append("thumbnail", thumbnail);
    }
  
    const token = localStorage.getItem("token");
  
    try {
      let response;
  
      if (selectedCategory) {
        formData.append("id_course_category", selectedCategory._id);
        response = await axios.put(editCategoryUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setMessage("Course Category updated successfully");
      } else {
        response = await axios.post(addCategoryUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setMessage("Course Category added successfully");
      }
  
      setSuccessPopup(true);
      handleCloseDialog();
  
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: selectedCategory
          ? "Course category updated successfully."
          : "Course category added successfully.",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      console.error("Error saving category:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save category. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  
  const fetchJobPositions = async () => {
    const token = localStorage.getItem('token');
    try {
      // Use the new jobPositionApiUrl
      const response = await axios.get(jobPositionApiUrl, {
        headers: {
          'Authorization': `${token}`,
        },
      });
  
      console.log('API Response:', response);  // Full response for debugging
      if (response.data && Array.isArray(response.data)) {
        setJobPositions(response.data);  // Ensure that you're getting an array
      } else {
        console.error('Invalid response structure:', response.data);
      }
    } catch (error) {
      console.error('Error fetching job positions:', error);
    }
  };  
  
  // Fetch departments when the component is mounted or updated
  useEffect(() => {
    fetchJobPositions();
  }, []);
   
  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
        // Tambahkan header Authorization pada permintaan DELETE
        await axios.delete(deleteCategoryUrl(id), {
          headers: {
            'Authorization': `Bearer ${token}`, // Tambahkan token di header
          },
        });
  
        // Update state setelah penghapusan berhasil
        setData(data.filter(cat => cat._id !== id));
        setMessage('Course Category deleted successfully');
        setSuccessPopup(true);
        fetchCategories(); // Refetch data setelah penghapusan
      } catch (error) {
        console.error('Error deleting category:', error);
        setAlert({
          open: true,
          message: 'Failed to delete category. Please try again.',
          severity: 'error',
        });
      }
    }
  };

  const handleThumbnailChange = async (event) => {
    const file = event.target.files[0];
    setThumbnail(file);
    console.log("Selected file:", file); // Log untuk memastikan file terpilih
  
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log("Preview URL:", reader.result); // Log URL preview
        setPreview(reader.result); // Perbarui state preview dengan data URL file
      };
      reader.readAsDataURL(file); // Membaca file untuk mendapatkan URL data
  
      try {
        const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
        // Jika ingin mengupload file ke server:
        const formData = new FormData();
        formData.append('thumbnail', file);
  
        const response = await axios.post(
          'https://api.academy.eurekagroup.id/upload/thumbnail',
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`, // Tambahkan token di header
              'Content-Type': 'multipart/form-data',
            },
          }
        );
  
        console.log('Upload response:', response.data); // Log hasil upload
  
        // Perbarui state thumbnail dengan URL yang diterima dari response
        setThumbnailUrl(response.data.url); // Asumsi response.data.url berisi URL thumbnail
  
      } catch (error) {
        console.error('Error uploading thumbnail:', error);
        // Tidak ada alert disini, cukup log error
      }
    }
  };  

  const filteredData = data.filter(cat =>
    cat.title_category && cat.title_category.toLowerCase().includes(searchText.toLowerCase())
  );
  console.log(filteredData);

  const generateSlug = (title) => {
    return title
      .toLowerCase() // Menjadikan huruf kecil
      .replace(/[^a-z0-9\s-]/g, '') // Menghapus karakter non-alfabet
      .replace(/\s+/g, '-') // Mengganti spasi dengan tanda minus
      .replace(/-+/g, '-'); // Menghapus tanda minus ganda
  };

  // Efek untuk meng-update slug ketika titleCategory berubah
  useEffect(() => {
    if (titleCategory) {
      setSlugCategory(generateSlug(titleCategory));
    } else {
      setSlugCategory('');
    }
  }, [titleCategory]);

  return (
    <>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <h1 style={{ color: '#2d3436', fontFamily: 'Poppins', fontSize: '20px' }}>
                    Course Category Management
                  </h1>
                  <Box>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      value={searchText}
                      onChange={handleSearchChange}
                      sx={{ marginRight: 2, borderRadius: '8px', backgroundColor: '#f5f5f5' }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog(null)}
                      sx={{
                        background: '#3f51b5',
                        color: 'white',
                        borderRadius: '20px',
                        '&:hover': { background: 'linear-gradient(45deg, #0984e3, #00b894)' },
                        padding: '10px 20px',
                      }}
                    >
                      Add Category
                    </Button>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              <TableContainer sx={{ marginTop: '-30px', overflow: 'visible' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none" align="left" sx={{ paddingY: '8px' }}>No</TableCell>
                      <TableCell padding="none" align="left" sx={{ paddingY: '8px' }}>Image</TableCell>
                      <TableCell padding="none" align="left" sx={{ paddingY: '8px' }}>Job Position</TableCell>
                      <TableCell padding="none" align="left" sx={{ paddingY: '8px' }}>Category Title</TableCell>
                      <TableCell padding="none" align="left" sx={{ paddingY: '8px' }}>Slug</TableCell>
                      <TableCell padding="none" align="left" sx={{ paddingY: '8px' }}>Status</TableCell>
                      <TableCell padding="none" sx={{ paddingY: '8px' }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
  {filteredData
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((cat, index) => {
      console.log(cat);  // Log each category to verify correct mapping
      return (
        <TableRow key={cat._id}>
          <TableCell padding="none" align="left">{page * rowsPerPage + index + 1}</TableCell>
          <TableCell>
            {cat.thumbnail && (
              <img
                src={`https://api.academy.eurekagroup.id${cat.thumbnail}`}
                alt="Thumbnail"
                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
              />
            )}
          </TableCell>
          <TableCell padding="none" align="left">
  {/* Mengecek apakah id_jobposition bernilai null */}
  {cat.id_jobposition === null || cat.id_jobposition.length === 0 ? (
    <div>ALL Position</div> // Jika id_jobposition null atau kosong, tampilkan "ALL Position"
  ) : cat.id_jobposition === 'all' ? (
    <div>All Job Positions</div> // Jika "Select All" dipilih, tampilkan "All Job Positions"
  ) : (
    // Menampilkan nama Job_positions dari setiap item dalam array jika tidak "Select All"
    cat.id_jobposition?.map((job, index) => (
      <div key={index}>{job.Job_positions || "Unknown"}</div>
    ))
  )}
</TableCell>
          <TableCell padding="none" align="left">{cat.title_category}</TableCell>
          <TableCell padding="none" align="left">{cat.slug_category}</TableCell>
          <TableCell>{cat.is_active ? 'Active' : 'Inactive'}</TableCell>
          <TableCell padding="none" align="right">
            <IconButton onClick={() => handleOpenDialog(cat)}>
              <EditIcon color="primary" />
            </IconButton>
            <IconButton onClick={() => handleDeleteClick(cat._id)}>
              <DeleteIcon sx={{ color: '#f44336' }} />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    })}
</TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar open={successPopup} autoHideDuration={3000} onClose={() => setSuccessPopup(false)}>
        <Box
          sx={{
            padding: '10px 20px',
            borderRadius: '8px',
            backgroundColor: '#4CAF50',
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {message}
        </Box>
      </Snackbar>

      {/* Add/Edit Category Dialog */}
{errorMessage && (
  <Alert severity="error" sx={{ marginBottom: '16px' }}>
    {errorMessage}
  </Alert>
)}

<Dialog
  open={openDialog}
  onClose={(e, reason) => {
    if (reason === 'backdropClick') return;
  }}
>
  <DialogTitle>{selectedCategory ? 'Edit' : 'Add'} Category</DialogTitle>
  <DialogContent>
    <Box 
      sx={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '16px', 
        marginBottom: '16px' 
      }}
    >
      <TextField
        label="Category Title"
        fullWidth
        variant="outlined"
        value={titleCategory}
        onChange={(e) => setTitleCategory(e.target.value)}
        error={errors.titleCategory}
        helperText={errors.titleCategory ? 'Title is required' : ''}
      />
      
      <TextField
        label="Slug"
        fullWidth
        variant="outlined"
        value={slugCategory}
        onChange={(e) => setSlugCategory(e.target.value)}
        error={errors.slugCategory}
        helperText={errors.slugCategory ? 'Slug is required' : ''}
      />
      
      <FormControl fullWidth>
        <Autocomplete
          multiple
          options={[{ _id: 'all', Job_positions: 'Select All' }, ...jobPositions]}
          getOptionLabel={(option) => option.Job_positions}
          value={selectedJobPositions}
          onChange={(event, newValue) => {
            if (newValue.some(job => job._id === 'all')) {
              setSelectedJobPositions([{ _id: 'all', Job_positions: 'Select All' }]);
            } else {
              setSelectedJobPositions(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Departments" variant="outlined" />
          )}
          noOptionsText="No Department available"
          isOptionEqualToValue={(option, value) => option._id === value._id}
          error={errors.selectedJobPositions}
          helperText={errors.selectedJobPositions ? 'At least one department is required' : ''}
        />
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Status</InputLabel>
        <Select
          value={isActive}
          onChange={(e) => setIsActive(e.target.value)}
          label="Status"
          error={errors.isActive}
        >
          <MenuItem value={true}>Active</MenuItem>
          <MenuItem value={false}>Inactive</MenuItem>
        </Select>
        {errors.isActive && <FormHelperText>Status is required</FormHelperText>}
      </FormControl>
    </Box>

    {/* Description tetap 1 kolom penuh */}
    <TextField
      label="Description"
      fullWidth
      variant="outlined"
      multiline
      rows={4}
      value={descCategory}
      onChange={(e) => setDescCategory(e.target.value)}
      error={errors.descCategory}
      helperText={errors.descCategory ? 'Description is required' : ''}
      sx={{ marginBottom: '16px' }}
    />

    {/* Input file dan preview */}
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'center' }}>
      <input
        type="file"
        accept="image/*"
        onChange={handleThumbnailChange}
      />
      {preview && (
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={preview}
            alt="Thumbnail Preview"
            style={{
              width: '150px',
              height: '150px',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
        </Box>
      )}
    </Box>
    {errors.thumbnail && (
      <FormHelperText error>Thumbnail image is required</FormHelperText>
    )}
  </DialogContent>
  
  <DialogActions>
    <Button onClick={handleCloseDialog} color="secondary" disabled={isLoading}>Cancel</Button>
    <Button onClick={handleSaveCategory} color="primary" disabled={isLoading}>
      {isLoading ? 'Loading...' : 'Save'}
    </Button>
  </DialogActions>
</Dialog>

    </>
  );
};

export default CategoryCoursePage;